import classNames from "classnames";
import { FC, useContext, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { usePageContext } from "../../context/pageContext";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import useProcessingRequestApi from "../../http/processingRequest/api";
import useFinalItemApi from "../../http/processingRequestFinalItem/api";
import { PaginationQuery } from "../../types";
import DisplayColumn from "../ui/DisplayColumn";
import GeneralData from "./GeneralData/GeneralData";
import styles from "./ProcessingRequestDetail.module.css";
import RequestHistory from "./RequestHistory";
import RequestLabelsAndPackagings from "./RequestLabelsAndPackagings";
import UsedProducts from "./UsedProducts";

const ProcessingRequestDetail: FC = () => {
  const { id } = useParams<{ id: string }>();
  const processingRequestApi = useProcessingRequestApi();
  const finalItemApi = useFinalItemApi();
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const pageContext = usePageContext();
  const filters = useListFilters({ isSearchable: false, limit: 100 });
  const debouncedValue = useDebounce(filters.state.q);

  const boxContainer = useRef<HTMLDivElement>(null);

  const query: PaginationQuery = { ...filters.state, q: debouncedValue };

  const result = processingRequestApi.useProcessingRequestDetail(id!);
  const [data, refetch, error] = [result.data, result.refetch, result.error];

  const resultFinalItems = finalItemApi.useProcessingRequestFinalItems(query, id!);
  const [finalItems, isLoadingFinalItems, finalItemsError] = [
    resultFinalItems.data,
    resultFinalItems.isLoading,
    resultFinalItems.error,
  ];

  useEffect(() => {
    error && dispatch(showErrorNotification(i18n, error));
  }, [error]);

  useEffect(() => {
    if (id) {
      pageContext.setPage({ url: "requests", title: i18n.t.common.requests }, id);
      refetch();
    }
  }, [id, i18n.language, refetch]);

  const totalUsedProductsCost =
    finalItems?.items.reduce((total, finalItem) => {
      const initialItemsCost = finalItem.initialItems.reduce((sum, entry) => {
        return sum + entry.cost * entry.quantity;
      }, 0);
      return total + initialItemsCost;
    }, 0) ?? 0;

  if (!data && !error && !isLoadingFinalItems && !finalItems) {
    return;
  }

  return (
    data && (
      <div className={styles.container}>
        <div className={classNames(styles.boxContainer, styles.left)} ref={boxContainer}>
          <DisplayColumn gap className={styles.minHeight}>
            <GeneralData request={data} totalUsedProductsCost={totalUsedProductsCost} trigger={refetch} />
            <UsedProducts
              finalItems={finalItems}
              finalItemsError={finalItemsError}
              totalUsedProductsCost={totalUsedProductsCost}
              isKit={data.isKit}
            />

            <RequestLabelsAndPackagings
              requestStatus={data.status}
              request={data}
              onUpdate={(updateData) => {
                updateData && refetch();
              }}
            />
          </DisplayColumn>
        </div>
        <div className={classNames(styles.boxContainer, styles.maxWidth, styles.right)}>
          <RequestHistory request={data} />
        </div>
      </div>
    )
  );
};

export default ProcessingRequestDetail;
